
import {
  defineComponent, onBeforeMount, reactive, ref,
} from 'vue';
import { useTelaBase } from '@/core/composables/TelaBase';
import Icone from '@/core/components/Icone.vue';
import TituloPadrao from '@/core/components/Tela/TituloPadrao.vue';
import MensagemSemDados from '@/core/components/Tela/MensagemSemDados.vue';
import ServicoRelatorioFinanceiro from '@/servicos/Relatorios/ServicoRelatoriosFinanceiros';
import SelecionarConta from '@/components/Cadastros/Financeiro/SelecionarConta.vue';
import { ETipoPermissao } from '@/models/Enumeradores/MeuSistema/Usuarios/ETipoPermissao';
import { IFiltrosRelatorioFluxoFinanceiro } from '@/models/Relatorios/Financeiro/FluxoFinanceiro/IFiltrosRelatorioFluxoFinanceiro';
import { DTORelatorioFluxoFinanceiro } from '@/models/Relatorios/Financeiro/FluxoFinanceiro/IDTORelatorioFluxoFinanceiro';
import { ETipoArquivo } from '@/core/models/Enumeradores/ETipoArquivo';
import { EStatusRetornoRequisicao } from '@/core/models/IRetornoRequisicao';
import { IArquivoPdf } from '@/core/models/IArquivoPdf';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';
import storeSistema from '@/store/storeSistema';
import SelecionarEmpresa from '@/components/MeuSistema/Empresas/SelecionarEmpresa.vue';
import SelecionarData from '@/core/components/Tela/SelecionarData.vue';
import CampoNumerico from '@/core/components/Tela/CampoNumerico.vue';
import ServicoConta from '@/servicos/Cadastros/Financeiro/ServicoConta';
import PreviewPdf from '@/core/components/Modal/PreviewPdf.vue';
import ServicoCacheFiltros from '@/servicos/Relatorios/ServicoCacheFiltros';
import { ICacheFiltros } from '@/models/Relatorios/ICacheFiltros';

export default defineComponent({
  name: 'RelatorioFluxoFinanceiro',
  components: {
    Icone,
    MensagemSemDados,
    TituloPadrao,
    SelecionarConta,
    SelecionarEmpresa,
    SelecionarData,
    CampoNumerico,
    PreviewPdf,
  },
  setup() {
    const servicoRelatorioFinanceiro = new ServicoRelatorioFinanceiro();
    const servicoConta = new ServicoConta();
    const servicoCacheFiltros = new ServicoCacheFiltros();
    const {
      telaBase, obterPermissoes, preencherEmpresasDisponiveis, verificaConceitoParaApresentarEmpresas,
      defineEmpresasSelecionadasComPermissao, filtrarPermissaoDadosUsuarioMultiEmpresas, preencherDadosRota,
      apresentarMensagemAlerta, apresentarMensagemErro,
    } = useTelaBase();

    const state = reactive({
      filtros: {} as IFiltrosRelatorioFluxoFinanceiro,
      resultado: {} as DTORelatorioFluxoFinanceiro,
      arquivosPdf: [] as IArquivoPdf[],
      cache: {} as ICacheFiltros,
    });

    const DTORelatorio = ref<DTORelatorioFluxoFinanceiro>({
      codigoEmpresa: 0,
      nomeEmpresa: '',
      logoEmpresa: '',
      parametros: {
        empresa: 0,
        contas: [] as number[],
        saldoBancario: 0,
        dataBase: new Date().toISOString().split('T')[0],
        quantidadeMeses: 4,
      },
      saldoBancario: {
        saldoComLimites: 0,
        saldoSemLimites: 0,
      },
      despesasMensais: {
        despesas: [],
        totalDespesasMes: 0,
        saldoGeralSemLimites: 0,
      },
      previsaoRecebimento: {
        cobSimples: 0,
        pedidosPendentes: 0,
        totalPrevisao: 0,
      },
      despesasFuturas: {
        despesaFutura: [],
        totalPagar: 0,
        saldoFinal: 0,
      },
      duplicatasCarteiraMaisUmAno: {
        faturadosMaisUmAno: 0,
      },
      saldoFuturo: {
        saldoComFaturado: 0,
      },
      duplicatasCarteira: {
        carteiraDesconto180: 0,
        carteiraDescontoMais180: 0,
        totalCarteiraDesconto: 0,
        totalPrevisaoMaisCarteira: 0,
        saldoDisponivel: 0,
      },
    });
    function formatarMoeda(valor: number) {
      return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }

    async function gerarRelatorio() {
      telaBase.carregando = true;
      if (!state.filtros.empresa) {
        state.filtros.empresa = telaBase.empresasSelecionadas[0] || 0;
      }
      if (state.cache.codigo > 0) {
        state.cache.valor = JSON.stringify(state.filtros) as string;
        await servicoCacheFiltros.alterarCacheFiltros(state.cache);
      } else {
        state.cache.chave = 'RelatorioFluxoFinanceiroIndex';
        state.cache.codigoEmpresa = telaBase.empresasSelecionadas[0] || 0;
        state.cache.valor = JSON.stringify(state.filtros) as string;
        await servicoCacheFiltros.incluirCacheFiltros(state.cache);
      }
      state.resultado = await servicoRelatorioFinanceiro.obterFluxoFinanceiro(state.filtros);
      DTORelatorio.value = state.resultado;
      telaBase.carregando = false;
    }

    async function buscarRelatorio() {
      state.resultado = await servicoRelatorioFinanceiro.obterFluxoFinanceiro(state.filtros);
      telaBase.carregando = false;
    }
    onBeforeMount(async () => {
      preencherDadosRota();
      await obterPermissoes(ETipoPermissao.Dados);
      await preencherEmpresasDisponiveis();
      await defineEmpresasSelecionadasComPermissao();
      verificaConceitoParaApresentarEmpresas();
      telaBase.empresasSelecionadas = storeSistema.getters.empresasUsuarioAutenticado();
      state.filtros.empresa = telaBase.empresasSelecionadas[0] || 0;
      state.filtros.contas = await servicoConta.obterCodigosContasFluxoFinanceiro(state.filtros.empresa);
      state.cache = await servicoCacheFiltros.obterCacheFiltros('RelatorioFluxoFinanceiroIndex', state.filtros.empresa);
      if (state.cache !== null && state.cache.codigo > 0) {
        state.filtros = JSON.parse(state.cache.valor) as IFiltrosRelatorioFluxoFinanceiro;
      } else {
        state.filtros.empresa = telaBase.empresasSelecionadas[0] || 0;
        state.filtros.saldoBancario = 0;
        state.filtros.dataBase = new Date().toISOString().split('T')[0];
        state.filtros.quantidadeMeses = 4;
        telaBase.permissaoDados = await filtrarPermissaoDadosUsuarioMultiEmpresas(telaBase.empresasSelecionadas);
      }
    });

    async function atualizarSelecaoEmpresas() {
      telaBase.empresasSelecionadas = [] as number[];
      telaBase.empresasSelecionadas.push(state.filtros.empresa);
      telaBase.permissaoDados = await filtrarPermissaoDadosUsuarioMultiEmpresas(telaBase.empresasSelecionadas);
      await buscarRelatorio();
    }

    async function imprimir(tipoArquivoRelatorio:ETipoArquivo) {
      telaBase.carregando = true;
      if (!state.resultado === undefined) {
        await buscarRelatorio();
      }
      const retornoRelatorio = await servicoRelatorioFinanceiro.obterArquivoFluxoFinanceiro(tipoArquivoRelatorio, state.filtros);
      telaBase.carregando = false;
      if (retornoRelatorio.status === EStatusRetornoRequisicao.Sucesso) {
        if (tipoArquivoRelatorio === ETipoArquivo.PDF) {
          state.arquivosPdf = [{ nome: '', link: retornoRelatorio.link } as IArquivoPdf];
        } else {
          UtilitarioGeral.downloadArquivo(retornoRelatorio.link);
        }
      } else if (retornoRelatorio.status === EStatusRetornoRequisicao.Alerta) {
        apresentarMensagemAlerta(retornoRelatorio.mensagem);
      } else if (retornoRelatorio.status === EStatusRetornoRequisicao.Erro) {
        apresentarMensagemErro(retornoRelatorio.mensagem);
      }
    }

    return {
      telaBase,
      state,
      DTORelatorio,
      formatarMoeda,
      gerarRelatorio,
      atualizarSelecaoEmpresas,
      buscarRelatorio,
      imprimir,
      ETipoArquivo,
      UtilitarioData,
    };
  },
});
